import type {
  FieldType,
  RangedFieldTypes,
  RangedFieldTypesMappings,
} from '@dynatrace-sdk/client-query';

// in @dynatrace-sdk/client-query-v02, this type used to exist on every record, but was removed in @dynatrace-sdk/client-query
/**
 * @public
 */
export type FieldEntry = {
  name: string;
  type: FieldType['type'];
  elementType?: FieldType['type'];
};

type WithUndefinedType<T extends FieldEntry> = Omit<T, 'type'> & {
  type: FieldEntry['type'] | undefined;
};

const mapToFieldEntry =
  (mappings: RangedFieldTypesMappings) =>
  (key: string): WithUndefinedType<FieldEntry> => {
    // which only supports homogeneous arrays as of now
    const elementType = mappings[key]?.types?.[0]?.mappings.element?.type;

    return {
      name: key,
      type: mappings[key]?.type,
      ...(elementType ? { elementType } : {}),
    };
  };

const removeEmptyTypes = (
  field: WithUndefinedType<FieldEntry>,
): field is FieldEntry => field.type !== undefined;

/**
 * Gets Types for a record given a record index.
 * @param fieldTypes - Field types
 * @param recordIndex - Record index
 * @public
 */
export const getTypesForRecordIndex = (
  fieldTypes: RangedFieldTypes[],
  recordIndex: number,
): RangedFieldTypes | undefined => {
  let low = 0;
  let high = fieldTypes.length - 1;
  let mid;
  while (high >= low) {
    mid = low + Math.floor((high - low) / 2);
    const indexRange = fieldTypes[mid].indexRange;
    if (indexRange === undefined) {
      return;
    }
    const [lowerBoundIndexRange, upperBoundIndexRange] = indexRange;
    if (
      lowerBoundIndexRange <= recordIndex &&
      upperBoundIndexRange >= recordIndex
    ) {
      return fieldTypes[mid];
    }
    if (upperBoundIndexRange > recordIndex) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
};

/**
 * @public
 */
export const convertToFieldEntries = (type: RangedFieldTypes) => {
  return Object.keys(type.mappings)
    .map(mapToFieldEntry(type.mappings))
    .filter(removeEmptyTypes);
};

// this helper function creates a fields array that used to exist on every record
// in @dynatrace-sdk/client-query-v02, but does not exist in @dynatrace-sdk/client-query anymore
/**
 * @public
 */
export const getFieldsArrayForRecordIndex = (
  fieldTypes: RangedFieldTypes[],
  recordIndex: number,
) => {
  const typeEntry = getTypesForRecordIndex(fieldTypes, recordIndex);
  return typeEntry
    ? Object.keys(typeEntry.mappings)
        .map(mapToFieldEntry(typeEntry.mappings))
        .filter(removeEmptyTypes)
    : [];
};
